import React, { useEffect, useMemo, useState } from "react";
import useFetch from "../../hooks/fetchMSSQL-hook";
import { NotificationManager } from "react-notifications";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const VISUAL_W = 100;
const VISUAL_H = 150;

const FALLBACK_UNIT_BY_TYPE = {
  K: "szt.",
  W: "szt.",
  M: "szt.",
  H: "ml",
};

const ensureArray = (resp) => {
  const r = resp?.data ?? resp;
  if (Array.isArray(r)) return r;
  if (Array.isArray(r?.data)) return r.data;
  return [];
};

const deepCopy = (v) => JSON.parse(JSON.stringify(v ?? []));

const normalizeHex = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const hex = s.startsWith("#") ? s : `#${s}`;
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) return hex;
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  if (/^#[0-9a-fA-F]{8}$/.test(hex)) return hex;
  return null;
};

const normalizeUnit = (v) => {
  const s = String(v ?? "").trim();
  return s ? s : null;
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toWholeQty = (v) => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
};

const getProductDefaultQuantity = (product) => {
  const n = toWholeQty(product?.IngredientQuantity);
  return n !== null && n >= 0 ? n : 0;
};

const sortByContainerNo = (a, b) => {
  const an = toNum(a?.ContainerNo);
  const bn = toNum(b?.ContainerNo);

  if (an !== null && bn !== null) return an - bn;
  if (an !== null) return -1;
  if (bn !== null) return 1;

  return String(a?.ContainerNo || "").localeCompare(String(b?.ContainerNo || ""));
};

const sortItems = (list) => [...(list || [])].sort(sortByContainerNo);

const resolveUnit = (unit, ingredientType, ingredientTypesByCode) => {
  const direct = normalizeUnit(unit);
  if (direct) return direct;

  const type = String(ingredientType || "").trim().toUpperCase();
  const fromIngredientType = normalizeUnit(ingredientTypesByCode?.get(type)?.Unit);
  if (fromIngredientType) return fromIngredientType;

  return FALLBACK_UNIT_BY_TYPE[type] || "g";
};

const getNextNumericContainerNo = (items) => {
  const nums = (items || [])
    .map((x) => String(x?.ContainerNo || "").trim())
    .filter((x) => /^\d+$/.test(x))
    .map((x) => Number(x));

  if (!nums.length) return "1";
  return String(Math.max(...nums) + 1);
};

const getItemKey = (item) => String(item?.MachineIngredientId ?? item?.__tempId ?? "");

const normalizeForCompare = (list) =>
  sortItems(list).map((x) => ({
    MachineIngredientId: x?.MachineIngredientId ?? null,
    ContainerNo: String(x?.ContainerNo || ""),
    ProductId: Number(x?.ProductId || 0),
    Capacity: Number(x?.Capacity || 0),
    Quantity: Number(x?.Quantity || 0),
    Unit: x?.Unit || "",
  }));

const callIfFn = (fn, ...args) => {
  if (typeof fn === "function") {
    fn(...args);
  }
};

const ContainerVisual = ({ pct, borderColor, fillColor }) => {
  const WIN_LEFT = 16;
  const WIN_RIGHT = 16;
  const WIN_TOP = 18;
  const WIN_H = 115;
  const WIN_R = 12;

  return (
    <div
      style={{
        width: VISUAL_W,
        height: VISUAL_H,
        borderRadius: 18,
        border: `2px solid ${borderColor}`,
        background: "#f7f7f7",
        position: "relative",
        overflow: "hidden",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.02)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 22,
          background: "#ededed",
          borderBottom: "1px solid #d7d7d7",
        }}
      />

      <div
        style={{
          position: "absolute",
          left: WIN_LEFT,
          right: WIN_RIGHT,
          top: WIN_TOP,
          height: WIN_H,
          borderRadius: WIN_R,
          border: "1px solid rgba(0,0,0,0.14)",
          background: "rgba(255,255,255,0.55)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: `${pct}%`,
            minHeight: pct > 0 ? 4 : 0,
            borderRadius: WIN_R,
            opacity: 0.95,
            border: "1px solid rgba(0,0,0,0.10)",
            boxSizing: "border-box",
            backgroundColor: fillColor,
          }}
        />
      </div>

      <div
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: 0,
          width: 44,
          height: 14,
          borderRadius: "14px 14px 0 0",
          background: "#dedede",
          borderTop: "1px solid #cfcfcf",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: 52,
          left: "50%",
          transform: "translateX(-50%)",
          width: 58,
          height: 28,
          border: `2px solid ${borderColor}`,
          borderRadius: 999,
          background: "#fff",
          display: "grid",
          placeItems: "center",
          fontWeight: 900,
          fontSize: 12,
          boxShadow: "0 2px 0 rgba(0,0,0,0.08)",
        }}
      >
        {pct}%
      </div>
    </div>
  );
};

const AddPreviewCard = ({ item }) => {
  const quantity = Number(item?.Quantity || 0);
  const capacity = Number(item?.Capacity || 0);
  const pct = capacity > 0 ? clamp(Math.round((quantity / capacity) * 100), 0, 100) : 0;
  const color = normalizeHex(item?.IngredientTypeColorKey) || "#6cb2eb";

  return (
    <div className="text-center">
      <div style={{ width: VISUAL_W, margin: "0 auto" }}>
        <ContainerVisual pct={pct} borderColor="black" fillColor={color} />
      </div>

      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 900 }}>
        {item?.ContainerNo || "-"}
      </div>

      <div style={{ fontSize: 12, lineHeight: "14px" }}>
        {item?.Name || "-"}
      </div>

      <div style={{ fontSize: 11, color: "#666" }}>
        {quantity}/{capacity} {item?.Unit || ""}
      </div>
    </div>
  );
};

const ExistingContainerCard = ({ item }) => {
  const quantity = Number(item?.Quantity || 0);
  const capacity = Number(item?.Capacity || 0);

  const pctRaw = Number.isFinite(Number(item?.Percent))
    ? Number(item.Percent)
    : capacity > 0
      ? (quantity / capacity) * 100
      : 0;

  const pct = clamp(Math.round(pctRaw), 0, 100);
  const color = normalizeHex(item?.IngredientTypeColorKey) || "#6cb2eb";

  return (
    <div className="text-center">
      <div style={{ width: VISUAL_W, margin: "0 auto" }}>
        <ContainerVisual pct={pct} borderColor="black" fillColor={color} />
      </div>

      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 900 }}>
        {item?.ContainerNo || "-"}
      </div>

      <div style={{ fontSize: 12, lineHeight: "14px" }}>
        {item?.Name || "-"}
      </div>

      <div style={{ fontSize: 11, color: "#666" }}>
        {quantity}/{capacity} {item?.Unit || ""}
      </div>
    </div>
  );
};

export default function MachineIngredientsModal({
  open,
  handleClose,
  machineId,
  onSaved,
}) {
  const { fetchMssqlApi } = useFetch();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [mode, setMode] = useState("list"); // list | add | edit
  const [editingItemKey, setEditingItemKey] = useState(null);

  const [items, setItems] = useState([]);
  const [initialItems, setInitialItems] = useState([]);
  const [history, setHistory] = useState([]);

  const [products, setProducts] = useState([]);
  const [ingredientTypes, setIngredientTypes] = useState([]);
  const [uiError, setUiError] = useState(null);

  const [formState, setFormState] = useState({
    ProductId: "",
    Capacity: "",
    Quantity: "",
    ContainerNo: "",
  });

  const isFormMode = mode === "add" || mode === "edit";
  const isEditMode = mode === "edit";

  const ingredientTypesByCode = useMemo(() => {
    const map = new Map();
    (ingredientTypes || []).forEach((x) => {
      map.set(String(x?.Code || "").trim().toUpperCase(), x);
    });
    return map;
  }, [ingredientTypes]);

  const productsById = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      map.set(Number(p.ProductId), p);
    });
    return map;
  }, [products]);

  const selectedProduct = useMemo(() => {
    const id = Number(formState.ProductId);
    if (!Number.isFinite(id) || !id) return null;
    return productsById.get(id) || null;
  }, [formState.ProductId, productsById]);

  const editingItem = useMemo(() => {
    if (!isEditMode) return null;
    return items.find((x) => getItemKey(x) === editingItemKey) || null;
  }, [isEditMode, editingItemKey, items]);

  const previewSource = selectedProduct || editingItem || null;

  const capacityRaw = String(formState.Capacity ?? "").trim();
  const quantityRaw = String(formState.Quantity ?? "").trim();
  const normalizedContainerNo = String(formState.ContainerNo ?? "").trim();

  const capacityNum = capacityRaw === "" ? null : Number(capacityRaw);
  const quantityNum = quantityRaw === "" ? null : Number(quantityRaw);

  const previewUnit = useMemo(() => {
    return resolveUnit(
      previewSource?.IngredientTypeUnit ?? previewSource?.Unit,
      previewSource?.IngredientType,
      ingredientTypesByCode
    );
  }, [previewSource, ingredientTypesByCode]);

  const displayQuantity = useMemo(() => {
    if (quantityNum === null || !Number.isFinite(quantityNum)) return 0;
    return quantityNum;
  }, [quantityNum]);

  const previewPct = useMemo(() => {
    return capacityNum && capacityNum > 0
      ? clamp(Math.round((displayQuantity / capacityNum) * 100), 0, 100)
      : 0;
  }, [displayQuantity, capacityNum]);

  const previewItem = useMemo(() => {
    if (!previewSource) return null;

    return {
      ContainerNo: normalizedContainerNo,
      Name: previewSource?.Name || "",
      Quantity: displayQuantity,
      Capacity: capacityNum || 0,
      Unit: previewUnit,
      IngredientTypeColorKey: previewSource?.IngredientTypeColorKey,
    };
  }, [previewSource, normalizedContainerNo, displayQuantity, capacityNum, previewUnit]);

  const selectedProductError = useMemo(() => {
    if (!selectedProduct) return "Wybierz składnik";
    return null;
  }, [selectedProduct]);

  const quantityError = useMemo(() => {
    if (!selectedProduct) return null;

    if (quantityRaw === "") {
      return "Podaj ilość składnika";
    }

    if (!Number.isFinite(quantityNum) || quantityNum < 0) {
      return "Podaj poprawną ilość składnika";
    }

    if (Number.isFinite(capacityNum) && capacityNum > 0 && quantityNum > capacityNum) {
      return "Ilość składnika nie może być większa od pojemności kontenera";
    }

    return null;
  }, [selectedProduct, quantityRaw, quantityNum, capacityNum]);

  const capacityError = useMemo(() => {
    if (!selectedProduct) return null;

    if (capacityRaw === "") {
      return "Wybierz poprawną pojemność";
    }

    if (!Number.isFinite(capacityNum) || capacityNum <= 0) {
      return "Wybierz poprawną pojemność";
    }

    return null;
  }, [selectedProduct, capacityRaw, capacityNum]);

  const containerNoError = useMemo(() => {
    if (!selectedProduct) return null;

    if (!normalizedContainerNo) {
      return "Podaj numer kontenera";
    }

    if (normalizedContainerNo.length > 10) {
      return "Numer kontenera może mieć maksymalnie 10 znaków";
    }

    const exists = (items || []).some((x) => {
      const sameKey = getItemKey(x) === editingItemKey;
      if (isEditMode && sameKey) return false;

      return (
        String(x?.ContainerNo || "").trim().toUpperCase() === normalizedContainerNo.toUpperCase()
      );
    });

    if (exists) {
      return "Taki numer kontenera już istnieje";
    }

    return null;
  }, [selectedProduct, normalizedContainerNo, items, isEditMode, editingItemKey]);

  const formError =
    selectedProductError || quantityError || capacityError || containerNoError || null;

  const dirty = useMemo(() => {
    return JSON.stringify(normalizeForCompare(items)) !== JSON.stringify(normalizeForCompare(initialItems));
  }, [items, initialItems]);

  const canUndo = history.length > 0;

  const fetchAsPromise = (url, options = {}) =>
    new Promise((resolve) => {
      fetchMssqlApi(
        url,
        { hideNotification: true, showLoader: false, ...options },
        (resp) => resolve(ensureArray(resp)),
        () => resolve([])
      );
    });

  const buildLoadedItem = (row, productMap, ingredientTypeMap) => {
    const p = productMap.get(Number(row?.ProductId)) || {};

    const resolvedQuantity =
      toWholeQty(row?.Quantity) ??
      toWholeQty(row?.ProductIngredientQuantity) ??
      toWholeQty(p?.IngredientQuantity) ??
      0;

    const resolvedCapacity = toWholeQty(row?.Capacity) ?? 0;

    const pctRaw = Number.isFinite(Number(row?.Percent))
      ? Number(row.Percent)
      : resolvedCapacity > 0
        ? (resolvedQuantity / resolvedCapacity) * 100
        : 0;

    const ingredientType = row?.IngredientType ?? p?.IngredientType ?? "";
    const ingredientTypeUnit =
      normalizeUnit(row?.IngredientTypeUnit) ||
      normalizeUnit(p?.IngredientTypeUnit) ||
      normalizeUnit(ingredientTypeMap.get(String(ingredientType).toUpperCase())?.Unit) ||
      null;

    return {
      MachineIngredientId: row?.MachineIngredientId ?? null,
      MachineId: row?.MachineId ?? machineId,
      ContainerNo: row?.ContainerNo ?? "",
      ProductId: row?.ProductId ?? p?.ProductId ?? null,
      Name: row?.Name ?? p?.Name ?? "",
      IngredientType: ingredientType,
      IngredientTypeName: row?.IngredientTypeName ?? p?.IngredientTypeName ?? "",
      IngredientTypeColorKey: row?.IngredientTypeColorKey ?? p?.IngredientTypeColorKey ?? null,
      IngredientTypeUnit: ingredientTypeUnit,
      ProductIngredientQuantity:
        toWholeQty(row?.ProductIngredientQuantity) ?? toWholeQty(p?.IngredientQuantity) ?? null,
      Capacity: resolvedCapacity,
      Quantity: resolvedQuantity,
      Percent: pctRaw,
      Unit: resolveUnit(
        row?.Unit,
        ingredientType,
        ingredientTypeMap
      ),
      __tempId: `existing-${row?.MachineIngredientId ?? row?.ContainerNo ?? Math.random()}`,
    };
  };

  const resetForm = () => {
    setFormState({
      ProductId: "",
      Capacity: "",
      Quantity: "",
      ContainerNo: "",
    });
    setEditingItemKey(null);
    setUiError(null);
  };

  const pushHistory = (snapshot) => {
    setHistory((prev) => [...prev, deepCopy(snapshot)].slice(-50));
  };

  const loadData = async () => {
    if (!open || !machineId) return;

    setLoading(true);
    setUiError(null);
    setMode("list");
    setHistory([]);
    resetForm();

    try {
      const [productsRows, currentRows, ingredientTypesRows] = await Promise.all([
        fetchAsPromise("products", {
          method: "POST",
          data: {
            isIngredient: 1,
            isActive: 1,
            pageSize: 1000,
            pageNo: 1,
          },
        }),
        fetchAsPromise("machine-ingredients", {
          method: "GET",
          params: { machineId },
        }),
        fetchAsPromise("ingredient-types", {
          method: "GET",
          params: { isActive: 1 },
        }),
      ]);

      const normalizedIngredientTypes = (ingredientTypesRows || [])
        .map((x) => ({
          ...x,
          Code: String(x?.Code || "").trim().toUpperCase(),
          Unit: normalizeUnit(x?.Unit),
        }))
        .filter((x) => x.Code);

      const ingredientTypeMap = new Map();
      normalizedIngredientTypes.forEach((x) => {
        ingredientTypeMap.set(x.Code, x);
      });

      const normalizedProducts = (productsRows || [])
        .filter(
          (p) =>
            Number(p?.IsIngredient) === 1 &&
            p?.IngredientType !== null &&
            String(p?.IngredientType || "").trim() !== ""
        )
        .map((p) => ({
          ...p,
          IngredientQuantity: toWholeQty(p?.IngredientQuantity),
          IngredientTypeColorKey: p?.IngredientTypeColorKey ?? null,
          IngredientTypeUnit: resolveUnit(
            p?.IngredientTypeUnit,
            p?.IngredientType,
            ingredientTypeMap
          ),
        }))
        .sort((a, b) => String(a?.Name || "").localeCompare(String(b?.Name || "")));

      const productMap = new Map();
      normalizedProducts.forEach((p) => {
        productMap.set(Number(p.ProductId), p);
      });

      const normalizedItems = sortItems(
        (currentRows || []).map((row) => buildLoadedItem(row, productMap, ingredientTypeMap))
      );

      setIngredientTypes(normalizedIngredientTypes);
      setProducts(normalizedProducts);
      setItems(normalizedItems);
      setInitialItems(deepCopy(normalizedItems));
    } catch (err) {
      console.error("MachineIngredientsModal loadData error:", err);
      setIngredientTypes([]);
      setProducts([]);
      setItems([]);
      setInitialItems([]);
      setUiError("Nie udało się pobrać danych składników");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, machineId]);

  const handleModalClose = () => {
    if (saving) return;

    if (dirty) {
      const ok = window.confirm("Masz niezapisane zmiany. Czy chcesz zamknąć modal?");
      if (!ok) return;
    }

    setMode("list");
    resetForm();
    callIfFn(handleClose);
  };

  const beginAdd = () => {
    setUiError(null);
    setEditingItemKey(null);
    setFormState({
      ProductId: "",
      Capacity: "",
      Quantity: "",
      ContainerNo: getNextNumericContainerNo(items),
    });
    setMode("add");
  };

  const beginEdit = (item) => {
    setUiError(null);
    setEditingItemKey(getItemKey(item));
    setFormState({
      ProductId: String(item?.ProductId ?? ""),
      Capacity: String(item?.Capacity ?? ""),
      Quantity: String(item?.Quantity ?? ""),
      ContainerNo: String(item?.ContainerNo ?? ""),
    });
    setMode("edit");
  };

  const cancelForm = () => {
    resetForm();
    setMode("list");
  };

  const handleProductChange = (e) => {
    const value = e.target.value;
    const nextProduct = productsById.get(Number(value)) || null;

    setUiError(null);

    setFormState((prev) => ({
      ...prev,
      ProductId: value,
      Quantity: isEditMode
        ? prev.Quantity
        : (value ? String(getProductDefaultQuantity(nextProduct)) : ""),
    }));
  };

  const handleCapacityChange = (e) => {
    const value = e.target.value;
    setUiError(null);
    setFormState((prev) => ({
      ...prev,
      Capacity: value,
    }));
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    setUiError(null);
    setFormState((prev) => ({
      ...prev,
      Quantity: value,
    }));
  };

  const handleContainerNoChange = (e) => {
    const value = String(e.target.value ?? "").slice(0, 10);
    setUiError(null);
    setFormState((prev) => ({
      ...prev,
      ContainerNo: value,
    }));
  };

  const addIngredient = () => {
    if (formError) {
      setUiError(formError);
      return;
    }

    const product = selectedProduct;
    if (!product) return;

    const nextQuantity = Number(formState.Quantity);
    const nextCapacity = Number(formState.Capacity);
    const nextPercent =
      nextCapacity > 0
        ? clamp(Math.round((nextQuantity / nextCapacity) * 100), 0, 100)
        : 0;

    const nextItem = {
      MachineIngredientId: null,
      MachineId: machineId,
      ContainerNo: normalizedContainerNo,
      ProductId: Number(product.ProductId),
      Name: product.Name,
      IngredientType: product.IngredientType,
      IngredientTypeName: product.IngredientTypeName,
      IngredientTypeColorKey: product.IngredientTypeColorKey,
      IngredientTypeUnit: product.IngredientTypeUnit,
      ProductIngredientQuantity: getProductDefaultQuantity(product),
      Capacity: nextCapacity,
      Quantity: nextQuantity,
      Percent: nextPercent,
      Unit: resolveUnit(
        product.IngredientTypeUnit,
        product.IngredientType,
        ingredientTypesByCode
      ),
      __tempId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };

    pushHistory(items);
    setItems((prev) => sortItems([...prev, nextItem]));
    setMode("list");
    resetForm();
  };

  const saveEditedIngredient = () => {
    if (formError) {
      setUiError(formError);
      return;
    }

    const product = selectedProduct;
    if (!product || !editingItem) return;

    const nextQuantity = Number(formState.Quantity);
    const nextCapacity = Number(formState.Capacity);
    const nextPercent =
      nextCapacity > 0
        ? clamp(Math.round((nextQuantity / nextCapacity) * 100), 0, 100)
        : 0;

    pushHistory(items);

    setItems((prev) =>
      sortItems(
        prev.map((item) => {
          if (getItemKey(item) !== editingItemKey) return item;

          return {
            ...item,
            ContainerNo: normalizedContainerNo,
            ProductId: Number(product.ProductId),
            Name: product.Name,
            IngredientType: product.IngredientType,
            IngredientTypeName: product.IngredientTypeName,
            IngredientTypeColorKey: product.IngredientTypeColorKey,
            IngredientTypeUnit: product.IngredientTypeUnit,
            ProductIngredientQuantity: getProductDefaultQuantity(product),
            Capacity: nextCapacity,
            Quantity: nextQuantity,
            Percent: nextPercent,
            Unit: resolveUnit(
              product.IngredientTypeUnit,
              product.IngredientType,
              ingredientTypesByCode
            ),
          };
        })
      )
    );

    setMode("list");
    resetForm();
  };

  const submitForm = () => {
    if (isEditMode) {
      saveEditedIngredient();
      return;
    }

    addIngredient();
  };

  const removeIngredient = (item) => {
    pushHistory(items);
    setItems((prev) => prev.filter((x) => getItemKey(x) !== getItemKey(item)));
  };

  const resetIngredients = () => {
    if (!items.length) return;
    pushHistory(items);
    setItems([]);
  };

  const undoLastAction = () => {
    setHistory((prev) => {
      if (!prev.length) return prev;

      const last = prev[prev.length - 1];
      setItems(deepCopy(last));

      return prev.slice(0, -1);
    });
  };

  const saveIngredients = () => {
    if (saving) return;

    const payload = sortItems(items).map((x) => ({
      MachineIngredientId: x?.MachineIngredientId ?? null,
      ContainerNo: String(x?.ContainerNo || ""),
      ProductId: Number(x?.ProductId),
      Capacity: Number(x?.Capacity),
      Quantity: Number(x?.Quantity ?? 0),
      Unit: x?.Unit || null,
    }));

    setSaving(true);
    setUiError(null);

    fetchMssqlApi(
      "machine-ingredient",
      {
        method: "POST",
        hideNotification: true,
        data: {
          MachineId: Number(machineId),
          Items: JSON.stringify(payload),
        },
      },
      () => {
        NotificationManager.success("Składniki automatu zostały zapisane", "Sukces", 2500);
        setInitialItems(deepCopy(items));
        setHistory([]);
        setSaving(false);
        setMode("list");
        resetForm();
        callIfFn(onSaved);
        callIfFn(handleClose);
      },
      (err) => {
        const msg =
          err?.response?.data?.message ||
          err?.data?.message ||
          "Nie udało się zapisać składników automatu";

        setSaving(false);
        setUiError(msg);
        NotificationManager.error(msg, "Błąd", 3000);
      }
    );
  };

  if (!open) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      role="dialog"
      style={{ background: "rgba(0,0,0,0.45)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable custom-scroll" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <div className="d-flex flex-column">
              <h5 className="modal-title mb-1">Edycja kontenerów</h5>
            </div>

            <button
              type="button"
              className="close"
              onClick={handleModalClose}
              disabled={saving}
            >
              <span>&times;</span>
            </button>
          </div>

          <div className="modal-body custom-scroll">
            {uiError && <div className="alert alert-danger">{uiError}</div>}

            {loading ? (
              <div className="text-center py-5 text-muted">Ładowanie danych...</div>
            ) : isFormMode ? (
              <div className="row">
                <div className="col-12 col-lg-7 mb-3 mb-lg-0">
                  <div className="border rounded p-3 h-100 bg-white">
                    <div className="font-weight-bold mb-3">
                      {isEditMode ? "Edycja" : "Dodaj składnik"}
                    </div>

                    <div className="form-group">
                      <label>Składnik</label>
                      <select
                        className="form-control"
                        value={formState.ProductId}
                        onChange={handleProductChange}
                      >
                        <option value="">Wybierz</option>
                        {products.map((p) => (
                          <option key={p.ProductId} value={p.ProductId}>
                            {p.Name} {p.IngredientTypeName ? `(${p.IngredientTypeName})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Ilość składnika</label>
                      <div className="input-group">
                        <input
                          type="number"
                          min={0}
                          step="1"
                          className={`form-control ${quantityError ? "is-invalid" : ""}`}
                          value={formState.Quantity}
                          onChange={handleQuantityChange}
                        />
                        <div className="input-group-append">
                          <span className="input-group-text">{previewUnit}</span>
                        </div>
                      </div>
                      {quantityError ? (
                        <div className="invalid-feedback d-block">{quantityError}</div>
                      ) : null}
                    </div>

                    <div className="form-group">
                      <label>Pojemność</label>
                      <div className="input-group">
                        <input
                          type="number"
                          min={0}
                          step="1"
                          className={`form-control ${capacityError ? "is-invalid" : ""}`}
                          value={formState.Capacity}
                          onChange={handleCapacityChange}
                        />
                        <div className="input-group-append">
                          <span className="input-group-text">{previewUnit}</span>
                        </div>
                      </div>
                      {capacityError ? (
                        <div className="invalid-feedback d-block">{capacityError}</div>
                      ) : null}
                    </div>

                    <div className="form-group mb-0">
                      <label>Numer kontenera</label>
                      <input
                        className={`form-control ${containerNoError ? "is-invalid" : ""}`}
                        value={formState.ContainerNo}
                        onChange={handleContainerNoChange}
                        maxLength={10}
                      />
                      {containerNoError ? (
                        <div className="invalid-feedback d-block">{containerNoError}</div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-lg-5">
                  <div className="border rounded p-3 h-100 bg-white d-flex flex-column">
                    <div className="font-weight-bold mb-3">Podgląd kontenera</div>

                    <div className="d-flex align-items-center justify-content-center flex-grow-1">
                      {previewItem ? (
                        <div>
                          <AddPreviewCard item={previewItem} />

                          <div className="text-center mt-3">
                            <div className="small text-muted">
                              Typ: {previewSource?.IngredientTypeName || previewSource?.IngredientType || "-"}
                            </div>
                            <div className="small text-muted">
                              Jednostka: {previewUnit}
                            </div>
                            <div className="small text-muted">
                              Stan wypełnienia: {previewPct}%
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted text-center">
                          Wybierz składnik, aby zobaczyć podgląd.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 10 }}>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={beginAdd}
                    disabled={saving}
                  >
                    <i className="fas fa-plus mr-2" />
                    Dodaj składnik
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={undoLastAction}
                    disabled={!canUndo || saving}
                  >
                    <i className="fas fa-undo mr-2" />
                    Cofnij
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={resetIngredients}
                    disabled={!items.length || saving}
                  >
                    <i className="fas fa-trash mr-2" />
                    Usuń wszystko
                  </button>

                  <span className="badge badge-light ml-auto px-3 py-2">
                    Składniki: {items.length}
                  </span>
                </div>

                {!items.length ? (
                  <div className="border rounded p-5 text-center text-muted">
                    Brak zdefiniowanych kontenerów. Dodaj pierwszy składnik.
                  </div>
                ) : (
                  <div className="row">
                    {sortItems(items).map((item) => (
                      <div key={getItemKey(item)} className="col-12 col-md-6 col-xl-4 mb-3">
                        <div className="border rounded p-3 h-100 bg-white d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="font-weight-bold" style={{ minWidth: 0 }}>
                              <div>{item.ContainerNo}</div>

                              <div
                                className="text-muted font-weight-normal"
                                style={{
                                  fontSize: 13,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {item.Name}
                              </div>
                            </div>

                            <div className="d-flex" style={{ gap: 6 }}>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => beginEdit(item)}
                                disabled={saving}
                                title="Edytuj"
                              >
                                <i className="fas fa-pen mr-1" />
                                Edytuj
                              </button>

                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => removeIngredient(item)}
                                disabled={saving}
                                title="Usuń"
                              >
                                <i className="fas fa-times mr-1" />
                                Usuń
                              </button>
                            </div>
                          </div>

                          <div className="d-flex justify-content-center mb-3">
                            <ExistingContainerCard item={item} />
                          </div>

                          <div className="small text-muted">
                            <div>
                              <strong>Typ:</strong>{" "}
                              {item.IngredientTypeName || item.IngredientType || "-"}
                            </div>
                            <div>
                              <strong>Jednostka:</strong>{" "}
                              {item.Unit || "-"}
                            </div>
                            <div>
                              <strong>Stan:</strong>{" "}
                              {Number(item.Quantity || 0)}/{Number(item.Capacity || 0)} {item.Unit || ""}
                            </div>
                            <div>
                              <strong>Pojemność:</strong>{" "}
                              {item.Capacity ?? "-"} {item.Unit || ""}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            {isFormMode ? (
              <>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={cancelForm}
                  disabled={saving}
                >
                  Wróć
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitForm}
                  disabled={!!formError || saving}
                >
                  {isEditMode ? "Zapisz" : "Dodaj składnik"}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleModalClose}
                  disabled={saving}
                >
                  Anuluj
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveIngredients}
                  disabled={saving || !dirty}
                >
                  {saving ? "Zapisywanie..." : "Zapisz"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}